import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, Paper, Snackbar, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Checkbox, useTheme, Typography, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { supabase } from '../supabaseClient';
import { useSecteur } from '../contexts/SecteurContext';
import { useAuth } from '../contexts/AuthContext';
import EventDialog from '../components/calendar/EventDialog';
import DirectionsSubwayIcon from '@mui/icons-material/DirectionsSubway'; // Icône Citymapper
import FileUploadIcon from '@mui/icons-material/FileUpload'; // Ajout de l'icône
import ical from 'ical.js'; // Ajout pour parser les fichiers ICS
import DescriptionIcon from '@mui/icons-material/Description'; // Ajout pour l'icône description

// Fonction utilitaire pour générer le lien Google Maps
function getGoogleMapsUrl({ destination, arrivalTime }) {
  if (!destination || !arrivalTime) return '#';

  const arrival = new Date(arrivalTime);
  arrival.setMinutes(arrival.getMinutes() - 15);
  const arrivalTimestamp = Math.floor(arrival.getTime() / 1000);

  const destQuery = encodeURIComponent(destination);

  // travelmode=transit pour transports en commun, arrival_time pour l'heure d'arrivée
  // On ne met pas origin, Google Maps demandera à l'utilisateur de le saisir
  return `https://www.google.com/maps/dir/?api=1&destination=${destQuery}&travelmode=transit&arrival_time=${arrivalTimestamp}`;
}

// Composant pour l'icône d'itinéraire sur un événement
function DirectionsButton({ event }) {
  const handleClick = (e) => {
    e.stopPropagation();
    if (!event.lieu) {
      alert("Lieu non renseigné.");
      return;
    }
    const url = getGoogleMapsUrl({
      destination: event.lieu,
      arrivalTime: event.start
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <IconButton
      size="small"
      title="Itinéraire Google Maps"
      onClick={handleClick}
      sx={{ ml: 1 }}
      disabled={!event.lieu}
    >
      <DirectionsSubwayIcon color="success" />
    </IconButton>
  );
}

export default function SectorCalendar() {
  const { selectedSecteur } = useSecteur();
  const { roles } = useAuth();
  const canEdit = roles?.can_manage_sector_calendar;
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [icsEvents, setIcsEvents] = useState([]);
  const [selectedIcsEvents, setSelectedIcsEvents] = useState([]);
  const [filteredPeriod, setFilteredPeriod] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Ajouter cette fonction pour réinitialiser complètement l'état de l'import
  const resetImportState = () => {
    setIcsEvents([]);
    setSelectedIcsEvents([]);
    setImportDialogOpen(false);
    setFilteredPeriod(null);
  };

  // Charger les événements
  const fetchEvents = useCallback(async () => {
    if (!selectedSecteur) return;
    
    try {
      const { data, error } = await supabase
        .from('sector_events')
        .select(`
          id,
          title,
          description,
          lieu,
          start_date,
          end_date,
          color,
          event_pointages (
            pointage (
              *,
              user_mandate:user_mandate_id (
                *,
                user:users(*)
              ),
              releve_codes (*),
              activities (*)
            )
          ),
          event_attachments (*),
          event_notes (*)
        `)
        .eq('sector_id', selectedSecteur);

      if (error) throw error;
      
      const formattedEvents = data.map(event => ({
        ...event,
        start: event.start_date,
        end: event.end_date,
        lieu: typeof event.lieu === 'string' ? event.lieu : ''
      }));

      setEvents(formattedEvents);
    } catch (error) {
      setSnackbar({ severity: 'error', message: 'Erreur lors du chargement des événements' });
    }
  }, [selectedSecteur]);

  // Fonction pour parser un texte ICS et détecter les doublons (utilisée pour local et distant)
  const parseIcsFile = async (file) => {
    try {
      const text = await file.text();
      if (!text.trim()) {
        throw new Error('Fichier vide');
      }

      const jcalData = ical.parse(text);
      if (!jcalData) {
        throw new Error('Format de fichier invalide');
      }

      const comp = new ical.Component(jcalData);
      const existingEvents = events.map(e => ({
        start: new Date(e.start),
        end: new Date(e.end),
        title: e.title
      }));

      let newEvents = comp.getAllSubcomponents('vevent').map(vevent => {
        const event = new ical.Event(vevent);
        return {
          uid: event.uid,
          title: event.summary,
          description: event.description,
          start: event.startDate.toJSDate(),
          end: event.endDate.toJSDate(),
          lieu: event.location
        };
      });
      
      const filteredEvents = newEvents.filter(newEv => 
        !existingEvents.some(existingEv =>
          existingEv.title === newEv.title &&
          existingEv.start.getTime() === newEv.start.getTime()
        )
      );

      filteredEvents.sort((a, b) => a.start - b.start);

      const today = new Date();
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const currentMonthEvents = filteredEvents.filter(ev => 
        ev.start.getMonth() === currentMonth && 
        ev.start.getFullYear() === currentYear
      );

      await setIcsEvents(filteredEvents);
      setImportDialogOpen(true);
      setSelectedIcsEvents(currentMonthEvents.map(ev => ev.uid));
    } catch (error) {
      console.error('Erreur parsing ICS:', error);
      setSnackbar({ severity: 'error', message: 'Erreur lors du parsing du fichier ICS' });
      resetImportState();
    }
  };

  const handleIcsFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      event.target.value = '';
      parseIcsFile(file).catch(error => {
        console.error('Erreur lors du traitement du fichier:', error);
        setSnackbar({ 
          severity: 'error', 
          message: `Erreur lors du traitement du fichier: ${error.message}` 
        });
      });
    }
  };

  const selectCurrentWeekEvents = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Lundi
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Dimanche
    endOfWeek.setHours(23, 59, 59, 999);
    
    const weekEvents = icsEvents.filter(ev => 
      ev.start >= startOfWeek && ev.start <= endOfWeek
    );
    setSelectedIcsEvents(weekEvents.map(ev => ev.uid));
    setFilteredPeriod('currentWeek');
  };

  const selectAllFutureEvents = () => {
    setSelectedIcsEvents(icsEvents.map(ev => ev.uid));
    setFilteredPeriod('all');
  };

  const selectLastMonthEvents = () => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1);
    
    const monthEvents = icsEvents.filter(ev => 
      ev.start.getMonth() === lastMonth.getMonth() && 
      ev.start.getFullYear() === lastMonth.getFullYear()
    );
    setSelectedIcsEvents(monthEvents.map(ev => ev.uid));
    setFilteredPeriod('lastMonth');
  };

  const selectCurrentMonthEvents = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    const monthEvents = icsEvents.filter(ev => 
      ev.start.getMonth() === currentMonth && 
      ev.start.getFullYear() === currentYear
    );
    setSelectedIcsEvents(monthEvents.map(ev => ev.uid));
    setFilteredPeriod('currentMonth');
  };

  const selectLastYearEvents = () => {
    const today = new Date();
    const lastYear = today.getFullYear() - 1;
    
    const yearEvents = icsEvents.filter(ev => 
      ev.start.getFullYear() === lastYear
    );
    setSelectedIcsEvents(yearEvents.map(ev => ev.uid));
    setFilteredPeriod('lastYear');
  };

  const selectCurrentYearEvents = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    
    const yearEvents = icsEvents.filter(ev => 
      ev.start.getFullYear() === currentYear
    );
    setSelectedIcsEvents(yearEvents.map(ev => ev.uid));
    setFilteredPeriod('currentYear');
  };

  const clearSelection = () => {
    setSelectedIcsEvents([]);
    setFilteredPeriod(null);
  };

  const handleImportSelectedIcsEvents = async () => {
    try {
      const eventsToImport = icsEvents.filter(ev => selectedIcsEvents.includes(ev.uid));
      for (const ev of eventsToImport) {
        await supabase.from('sector_events').insert({
          sector_id: selectedSecteur,
          title: ev.title,
          description: ev.description,
          lieu: ev.lieu,
          start_date: ev.start.toISOString(),
          end_date: ev.end?.toISOString() || ev.start.toISOString(),
          color: '#3788d8'
        });
      }
      resetImportState();
      fetchEvents();
      setSnackbar({ severity: 'success', message: 'Événements importés avec succès' });
    } catch (err) {
      setSnackbar({ severity: 'error', message: 'Erreur lors de l\'import ICS' });
    }
  };

  const handleDateSelect = (selectInfo) => {
    if (!canEdit) return;
    setSelectedEvent({
      start: selectInfo.start,
      end: selectInfo.end,
      allDay: selectInfo.allDay
    });
    setIsDialogOpen(true);
  };

  const handleEventClick = (clickInfo) => {
    setSelectedEvent(events.find(e => e.id === clickInfo.event.id));
    setIsDialogOpen(true);
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const { data, error } = await supabase.storage
        .from('event-attachments')
        .download(attachment.file_path);

      if (error) {
        setSnackbar({ severity: 'error', message: 'Erreur lors du téléchargement du fichier' });
        return;
      }

      const blob = new Blob([data], { type: attachment.file_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setSnackbar({ severity: 'error', message: 'Erreur inattendue lors du téléchargement' });
    }
  };

  // Signature modifiée pour accepter isMobile
  const renderListEventContent = (eventInfo, isMobile) => {
    const formatTime = (date) => {
      return new Date(date).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const startTime = formatTime(eventInfo.event.start);
    const endTime = formatTime(eventInfo.event.end);
    const timeText = `${startTime} - ${endTime}`;

    const pointages = eventInfo.event.extendedProps.event_pointages || [];
    const attachments = eventInfo.event.extendedProps.event_attachments || [];
    const notes = eventInfo.event.extendedProps.event_notes || [];
    const lieu = eventInfo.event.extendedProps.lieu;
    const description = eventInfo.event.extendedProps.description;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100vw',
          p: 0,
          m: 0,
          gap: isMobile ? 1 : 2,
          borderRadius: 1,
          backgroundColor: isMobile ? 'background.paper' : undefined,
          boxShadow: isMobile ? 0 : undefined,
          overflow: 'hidden',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        {/* Heure et titre */}
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 0.5 : 1,
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden'
        }}>
          <Typography
            variant="caption"
            sx={{
              color: 'primary.main',
              fontWeight: 'medium',
              fontSize: isMobile ? '1em' : '1.1em',
              minWidth: isMobile ? undefined : '80px',
              mb: isMobile ? 0.2 : 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {timeText}
          </Typography>
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{
              fontWeight: 'bold',
              lineHeight: 1.2,
              fontSize: isMobile ? '1.1em' : '1.2em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
              width: '100%',
              wordBreak: 'break-word',
              maxWidth: '100vw'
            }}
          >
            {eventInfo.event.title}
          </Typography>
        </Box>

        {/* Lieu et itinéraire */}
        {lieu && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.secondary',
            mt: 0.5,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            maxWidth: '100vw',
            overflow: 'hidden'
          }}>
            <DirectionsButton
              event={{
                ...eventInfo.event.extendedProps,
                title: eventInfo.event.title,
                start: eventInfo.event.start,
                lieu
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? '1em' : '1.05em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                maxWidth: isMobile ? '80vw' : 200,
                wordBreak: 'break-word'
              }}
            >
              {lieu}
            </Typography>
          </Box>
        )}

        {/* Description */}
        {description && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              whiteSpace: 'pre-wrap',
              backgroundColor: 'action.hover',
              p: 1,
              borderRadius: 1,
              fontSize: isMobile ? '0.98em' : '1em',
              maxWidth: isMobile ? '95vw' : '100%',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              overflow: 'hidden',
              boxSizing: 'border-box'
            }}
          >
            {description}
          </Typography>
        )}

        {/* Participants */}
        {pointages.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="subtitle2"
              color="primary.main"
              gutterBottom
              sx={{ fontSize: isMobile ? '1em' : '1.05em' }}
            >
              Participants ({pointages.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pointages.map((ep, idx) => {
                let fullName = 'Inconnu';
                let userId = undefined;
                if (ep.pointage?.user_mandate?.user) {
                  userId = ep.pointage.user_mandate.user.id;
                  fullName = ep.pointage.user_mandate.user.full_name && ep.pointage.user_mandate.user.full_name.trim() !== ''
                    ? ep.pointage.user_mandate.user.full_name
                    : 'Inconnu';
                } else if (ep.pointage?.user_mandate_id) {
                  userId = ep.pointage.user_mandate_id;
                  fullName = `user_mandate_id: ${userId}`;
                }

                const hours = ep.pointage?.hours;
                const code = ep.pointage?.releve_codes?.code || ep.pointage?.releve_codes?.label || '';
                const activity = ep.pointage?.activities?.label || '';
                return (
                  <Box key={idx} sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', pl: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        color: fullName === 'Inconnu' ? 'text.secondary' : undefined,
                        fontSize: isMobile ? '0.98em' : '1em'
                      }}
                    >
                      {fullName}
                    </Typography>
                    {(hours !== undefined || code || activity) && (
                      <Box sx={{
                        display: 'flex',
                        gap: isMobile ? 1 : 2,
                        flexWrap: 'wrap',
                        pl: isMobile ? 0 : 1,
                        flexDirection: isMobile ? 'column' : 'row'
                      }}>
                        {hours !== undefined && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.98em' : '1em' }}>
                            Heures : {hours}
                          </Typography>
                        )}
                        {code && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.98em' : '1em' }}>
                            Code : {code}
                          </Typography>
                        )}
                        {activity && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.98em' : '1em' }}>
                            Activité : {activity}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Pièces jointes */}
        {attachments.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle2"
              color="primary.main"
              gutterBottom
              sx={{ fontSize: isMobile ? '1em' : '1.05em' }}
            >
              Pièces jointes ({attachments.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              {attachments.map((att, idx) => {
                const fileName = att.file_name || att.name || att.filename || 'Fichier';
                return (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, maxWidth: isMobile ? '90vw' : 200, overflow: 'hidden' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: isMobile ? '0.97em' : { xs: '0.95em', md: '1em' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'normal' : 'nowrap',
                        maxWidth: isMobile ? '80vw' : 200,
                        wordBreak: 'break-word'
                      }}
                    >
                      {fileName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadAttachment(att);
                      }}
                      title="Télécharger"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M5 20h14v-2H5v2zm7-18v12.17l4.59-4.58L18 12l-6 6-6-6 1.41-1.41L11 14.17V2h2z" fill="currentColor" />
                      </svg>
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Notes */}
        {notes.length > 0 && (
          <Box sx={{ m: 0 }}>
            <Typography
              variant="subtitle2"
              color="primary.main"
              sx={{ mb: 0.2, fontSize: isMobile ? '1em' : '1.05em' }}
            >
              📝 Notes ({notes.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              {notes.map((note, idx) => (
                <Box key={idx} sx={{
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  p: 1,
                  pl: isMobile ? 1 : 2,
                  m: 0
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      whiteSpace: 'pre-wrap',
                      m: 0,
                      fontSize: isMobile ? '0.97em' : { xs: '0.95em', md: '1em' }
                    }}
                  >
                    {note.content || note.text || ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderEventContent = (eventInfo) => {
    const isListView = eventInfo.view.type.includes('list');

    if (isListView) {
      // Passer isMobile ici
      return renderListEventContent(eventInfo, isMobile);
    }

    const isMonth = eventInfo.view.type === 'dayGridMonth';
    const pointages = eventInfo.event.extendedProps.event_pointages || [];
    const notes = eventInfo.event.extendedProps.event_notes || [];
    const attachments = eventInfo.event.extendedProps.event_attachments || [];
    const description = eventInfo.event.extendedProps.description;
    const lieu = eventInfo.event.extendedProps.lieu;

    // Tooltips
    const participantsTooltip = pointages.length > 0 ? (
      <Box>
        {pointages.map((ep, idx) => {
          let fullName = 'Inconnu';
          if (ep.pointage?.user_mandate?.user?.full_name) {
            fullName = ep.pointage.user_mandate.user.full_name;
          }
          const hours = ep.pointage?.hours;
          const code = ep.pointage?.releve_codes?.code || ep.pointage?.releve_codes?.label;
          const activity = ep.pointage?.activities?.label;
          return (
            <Box key={idx} sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {fullName}
              </Typography>
              {(hours !== undefined || code || activity) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 1 }}>
                  {hours !== undefined && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      ⏱️ {hours}h
                    </Typography>
                  )}
                  {code && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      🏷️ {code}
                    </Typography>
                  )}
                  {activity && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      📋 {activity}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    ) : null;
    const notesTooltip = notes.length > 0 ? (
      <Box>
        {notes.map((note, idx) => (
          <Typography key={idx} variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', mb: 0.5 }}>
            {note.content || note.text || ''}
          </Typography>
        ))}
      </Box>
    ) : null;
    const attachmentsTooltip = attachments.length > 0 ? (
      <Box>
        {attachments.map((att, idx) => {
          const fileName = att.file_name || att.name || att.filename || 'Fichier';
          return (
            <Typography key={idx} variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              {fileName}
            </Typography>
          );
        })}
      </Box>
    ) : null;
    const lieuTooltip = lieu ? (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.95em', md: '1em' } }}>
        {lieu}
      </Typography>
    ) : null;
    const descriptionTooltip = description ? (
      <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', fontSize: { xs: '0.95em', md: '1em' } }}>
        {description}
      </Typography>
    ) : null;

    // Formatage robuste de l'heure (évite les erreurs si start/end sont invalides)
    let startTime = '';
    let endTime = '';
    let timeText = '';
    try {
      if (eventInfo.event.start) {
        const start = new Date(eventInfo.event.start);
        if (!isNaN(start)) startTime = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      }
      if (eventInfo.event.end) {
        const end = new Date(eventInfo.event.end);
        if (!isNaN(end)) endTime = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      }
      if (startTime && endTime && startTime !== endTime) {
        timeText = `${startTime} - ${endTime}`;
      } else if (startTime) {
        timeText = startTime;
      }
    } catch (e) {
      timeText = '';
    }

    // Styles adaptés pour la vue mois : 1 seule ligne pour le titre, icônes wrap, évite l'overflow

    return (
      <Box sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        p: isMonth ? '2px 2px' : 0.5,
        fontSize: isMonth ? '0.85em' : '0.95em',
        display: 'flex',
        flexDirection: 'column',
        gap: isMonth ? 0.1 : 0.5,
        lineHeight: 1.1
      }}>
        {/* Ligne 1 : Titre sur une seule ligne + icône description */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              fontSize: '1em',
              lineHeight: 1.2,
              marginBottom: isMonth ? '0px' : '2px',
              display: 'block'
            }}
            title={eventInfo.event.title}
          >
            {eventInfo.event.title}
          </Typography>
          {/* Icône description à droite du titre */}
          {isMonth && description && (
            <Tooltip title={descriptionTooltip} arrow placement="top" enterTouchDelay={0}>
              <span style={{ display: 'flex', alignItems: 'center', marginLeft: 4 }}>
                <DescriptionIcon
                  fontSize="small"
                  sx={{
                    color: theme.palette.primary.main,
                    verticalAlign: 'middle',
                    fontSize: '1.1em'
                  }}
                />
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Ligne 2 : Heures et lieu avec itinéraire */}
        {isMonth && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            width: '100%',
            minHeight: '1.2em',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {timeText && (
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 'medium',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  fontSize: '1em',
                  minWidth: '48px',
                  maxWidth: '60px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block'
                }}
                title={timeText}
              >
                {timeText}
              </Typography>
            )}
            {lieu && (
              <Tooltip title={lieuTooltip} arrow placement="top" enterTouchDelay={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, minWidth: 0, maxWidth: '60%', overflow: 'hidden' }}>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    minWidth: '16px',
                    maxWidth: '16px',
                    justifyContent: 'center'
                  }}>
                    <IconButton
                      size="small"
                      title="Itinéraire Google Maps"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!lieu) return;
                        const url = getGoogleMapsUrl({
                          destination: lieu,
                          arrivalTime: eventInfo.event.start
                        });
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{ ml: 0, p: '2px' }}
                      disabled={!lieu}
                    >
                      <DirectionsSubwayIcon color="success" sx={{ fontSize: '1.1em' }} />
                    </IconButton>
                  </span>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '1em',
                      maxWidth: '100px'
                    }}
                    title={lieu}
                  >
                    {lieu}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Ligne 3 : Participants, pièces jointes, notes */}
        {isMonth && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.3,
            width: '100%',
            minHeight: '1.2em',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            {pointages.length > 0 && (
              <Tooltip title={participantsTooltip} arrow placement="top" enterTouchDelay={0}>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontSize: '1em',
                    minWidth: '22px',
                    textAlign: 'center',
                    overflow: 'hidden'
                  }}
                >
                  👥 {pointages.length}
                </Typography>
              </Tooltip>
            )}
            {attachments.length > 0 && (
              <Tooltip title={attachmentsTooltip} arrow placement="top" enterTouchDelay={0}>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontSize: '1em',
                    minWidth: '22px',
                    textAlign: 'center',
                    overflow: 'hidden'
                  }}
                >
                  📎 {attachments.length}
                </Typography>
              </Tooltip>
            )}
            {notes.length > 0 && (
              <Tooltip title={notesTooltip} arrow placement="top" enterTouchDelay={0}>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontSize: '1em',
                    minWidth: '22px',
                    textAlign: 'center',
                    overflow: 'hidden'
                  }}
                >
                  📝 {notes.length}
                </Typography>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    );
  };

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return (
    <Box sx={{ 
      height: 'calc(100vh - 100px)',
      display: 'flex',
      flexDirection: 'column',
      p: 0,
      '& .fc': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        '& .fc-view': {
          flex: 1,
          minHeight: 0 // Permet à la vue de ne pas forcer de scroll interne
        },
        '& .fc-event': {
          backgroundColor: 'white',
          border: `1px solid ${theme.palette.primary.main}`,
          color: theme.palette.text.primary,
          cursor: 'pointer',
          minHeight: '25px',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: theme.shadows[1]
          }
        },
        '& .fc-daygrid-event': {
          whiteSpace: 'normal',
          marginBottom: '2px',
          maxHeight: '4.5em',
          overflow: 'hidden'
        },
        '& .fc-list-event-dot': {
          display: 'none !important'
        },
        '& .fc-list-event': {
          padding: '0 !important',
          margin: '0 !important',
          background: 'none !important',
          boxShadow: 'none !important'
        }
      }
    }}>
      <Paper elevation={3} sx={{
        p: 1,
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          {canEdit && (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedEvent(null);
                  setIsDialogOpen(true);
                }}
              >
                Créer un événement
              </Button>
              <Button
                variant="contained"
                component="label"
                startIcon={<FileUploadIcon />}
                sx={{ mr: 1 }}
              >
                IMPORTER des événements depuis un fichier .ics
                <input
                  type="file"
                  accept=".ics"
                  hidden
                  onChange={handleIcsFileSelect}
                />
              </Button>
            </>
          )}
        </Box>
        <FullCalendar
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView={'listMonth'}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,listMonth'
          }}
          views={{
            listMonth: {
              eventContent: arg => renderListEventContent(arg, isMobile),
              eventDisplay: 'block',
              displayEventTime: false,
              displayEventEnd: false,
              dayMaxEvents: false,
              noEventsContent: 'Aucun événement',
              dayHeaderFormat: { weekday: 'long', day: 'numeric', month: 'long', omitCommas: true }
            }
          }}
          height="auto" // Laisse la hauteur s'adapter au contenu, pas de scroll interne
          locale="fr"
          firstDay={1}
          buttonText={{
            today: "aujourd'hui",
            month: 'mois',
            week: 'semaine',
            day: 'jour',
            list: 'liste'
          }}
          moreLinkText="+ %d autres"
          noEventsText="Aucun événement"
          selectable={canEdit}
          select={handleDateSelect}
          events={events}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          eventClassNames={arg => {
            if (arg.view.type === 'listMonth') {
              return ['no-list-dot'];
            }
            return [];
          }}
        />
      </Paper>

      <Dialog 
        open={importDialogOpen} 
        onClose={() => resetImportState()}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Importer des événements ICS</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              size="small" 
              variant={filteredPeriod === 'all' ? 'contained' : 'outlined'}
              onClick={selectAllFutureEvents}
            >
              Tous les événements
            </Button>
            <Button 
              size="small" 
              variant={filteredPeriod === 'currentWeek' ? 'contained' : 'outlined'}
              onClick={selectCurrentWeekEvents}
            >
              Cette semaine
            </Button>
            <Button 
              size="small" 
              variant={filteredPeriod === 'lastMonth' ? 'contained' : 'outlined'}
              onClick={selectLastMonthEvents}
            >
              Mois dernier
            </Button>
            <Button 
              size="small" 
              variant={filteredPeriod === 'currentMonth' ? 'contained' : 'outlined'}
              onClick={selectCurrentMonthEvents}
            >
              Ce mois
            </Button>
            <Button 
              size="small" 
              variant={filteredPeriod === 'lastYear' ? 'contained' : 'outlined'}
              onClick={selectLastYearEvents}
            >
              Année dernière
            </Button>
            <Button 
              size="small" 
              variant={filteredPeriod === 'currentYear' ? 'contained' : 'outlined'}
              onClick={selectCurrentYearEvents}
            >
              Cette année
            </Button>
            <Button 
              size="small"
              variant="outlined"
              color="error"
              onClick={clearSelection}
            >
              Tout décocher
            </Button>
          </Box>
          <List>
            {icsEvents
              .filter(ev => {
                if (!filteredPeriod) return true;
                const today = new Date();
                switch (filteredPeriod) {
                  case 'currentWeek': {
                    const startOfWeek = new Date(today);
                    startOfWeek.setDate(today.getDate() - today.getDay() + 1);
                    startOfWeek.setHours(0, 0, 0, 0);
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    endOfWeek.setHours(23, 59, 59, 999);
                    return ev.start >= startOfWeek && ev.start <= endOfWeek;
                  }
                  case 'currentMonth':
                    return ev.start.getMonth() === today.getMonth() && 
                          ev.start.getFullYear() === today.getFullYear();
                  case 'lastMonth': {
                    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1);
                    return ev.start.getMonth() === lastMonth.getMonth() && 
                          ev.start.getFullYear() === lastMonth.getFullYear();
                  }
                  case 'currentYear':
                    return ev.start.getFullYear() === today.getFullYear();
                  case 'lastYear':
                    return ev.start.getFullYear() === today.getFullYear() - 1;
                  case 'all':
                  default:
                    return true;
                }
              })
              .map(ev => (
                <ListItem
                  key={ev.uid}
                  dense
                  button
                  onClick={() => {
                    setSelectedIcsEvents(sel =>
                      sel.includes(ev.uid)
                        ? sel.filter(id => id !== ev.uid)
                        : [...sel, ev.uid]
                    );
                  }}
                >
                  <>
                    <Checkbox checked={selectedIcsEvents.includes(ev.uid)} />
                    <ListItemText
                      primary={ev.title}
                      secondary={`${ev.start.toLocaleDateString()} ${ev.start.toLocaleTimeString()} - ${ev.lieu || ''}`}
                    />
                  </>
                </ListItem>
              ))}
            {icsEvents.length === 0 && (
              <ListItem>
                <ListItemText primary="Aucun événement à importer" />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resetImportState()}>Annuler</Button>
          <Button
            onClick={handleImportSelectedIcsEvents}
            disabled={selectedIcsEvents.length === 0}
            variant="contained"
          >
            Importer la sélection
          </Button>
        </DialogActions>
      </Dialog>

      <EventDialog
        open={isDialogOpen}
        event={selectedEvent}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedEvent(null);
        }}
        onSave={fetchEvents}
        canEdit={canEdit}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity}>{snackbar?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
